/**
 * The customer map sidebar's header strip: what the list is showing, how much
 * of it survived the filters, and the two controls that change the list itself
 * — its ordering and which extra details each row carries.
 *
 * The visible-against-total count is the header's real job: it is what tells an
 * operator that a half-empty map is the filters' doing and not an outage.
 */
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SORT_DIRECTION, SORT_FIELD } from '@sophocles/features/map/mapState';
import MapConfigureListButton from '@sophocles/views/map/components/MapConfigureListButton';

/**
 * @param {object} props
 * @param {string} props.title - what the rows are, in the customer's vocabulary.
 * @param {number} props.visibleCount - rows surviving the filters.
 * @param {number} props.totalCount - rows the customer has in total.
 * @param {{ field: string, direction: string }} props.sort
 * @param {(sort: { field: string, direction: string }) => void} props.onSortChange
 * @param {string[]} props.shownFields
 * @param {(fields: string[]) => void} props.onShownFieldsChange
 */
export default function MapSidebarHeader({
  title,
  visibleCount,
  totalCount,
  sort,
  onSortChange,
  shownFields,
  onShownFieldsChange,
}) {
  const { t } = useTranslation();

  const handleSortField = (_event, field) => {
    if (field) onSortChange({ ...sort, field });
  };

  const toggleDirection = () =>
    onSortChange({
      ...sort,
      direction: sort.direction === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC,
    });

  return (
    <Box
      sx={{
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        borderBottom: '1px solid var(--border-default)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {t('map.displayedCount', {
            count: visibleCount,
            total: totalCount,
            defaultValue: 'Displayed {{count}} ({{total}} total)',
          })}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={sort.field}
          onChange={handleSortField}
          aria-label={t('map.sortBy', 'Sort by')}
        >
          <ToggleButton value={SORT_FIELD.NAME}>{t('map.name', 'Name')}</ToggleButton>
          <ToggleButton value={SORT_FIELD.STATUS}>{t('map.status', 'Status')}</ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title={t('map.sortDirection', 'Sort direction')}>
          <IconButton size="small" onClick={toggleDirection} aria-label={t('map.sortDirection', 'Sort direction')}>
            {sort.direction === SORT_DIRECTION.ASC ? (
              <ArrowUpwardIcon fontSize="small" />
            ) : (
              <ArrowDownwardIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <MapConfigureListButton shownFields={shownFields} onShownFieldsChange={onShownFieldsChange} />
      </Box>
    </Box>
  );
}
